import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { href: "/how-it-works", label: "How It Works" },
  { href: "/who-we-help", label: "Who We Help" },
  { href: "/why-us", label: "Why Us" },
  { href: "/blog", label: "Resources" },
  { href: "/faq", label: "FAQ" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setIsOpen(false), [location.pathname]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-2xl border-b border-black/5 shadow-sm"
          : "bg-white/60 backdrop-blur-xl"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1" data-testid="nav-logo">
            <span className="text-xl font-medium tracking-tight text-ink">
              Apply<span className="text-brand">Strat</span>
            </span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/ /g, "-")}`}
                className={`nav-underline text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.href
                    ? "text-ink nav-active"
                    : "text-ink-secondary hover:text-ink"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Button asChild size="sm" className="hidden sm:inline-flex rounded-full shadow-sm">
              <Link to="/apply" data-testid="nav-apply-button">Apply Now</Link>
            </Button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-testid="nav-mobile-toggle"
              className="md:hidden text-ink-secondary hover:text-ink p-2 rounded-xl hover:bg-surface-raised transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-2xl border-b border-black/5">
          <div className="px-6 py-5 space-y-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/ /g, "-")}`}
                className="block py-3 px-3 rounded-xl text-ink-secondary hover:text-ink hover:bg-surface-raised font-medium text-sm transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3">
              <Button asChild className="w-full rounded-full">
                <Link to="/apply" data-testid="mobile-nav-apply-button">Apply Now</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
